/* GymRAG — Tailwind v4 + CSS 变量双轨（浅色清新主题，2026-08 用户要求从暗色切换） */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
@import "tailwindcss";

@theme {
  --font-sans: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  /* 品牌色板（浅色主题） */
  --color-bg: #f4f6f9;
  --color-surface: #ffffff;
  --color-surface-2: #eef1f5;
  --color-surface-3: #e3e8ef;
  --color-line: rgba(15, 23, 42, 0.08);
  --color-line-strong: rgba(15, 23, 42, 0.14);
  --color-text: #1e293b;
  --color-text-muted: #64748b;
  --color-text-faint: #94a3b8;
  --color-primary: #10b981;
  --color-primary-strong: #059669;
  --color-accent: #0ea5e9;
  --color-danger: #ef4444;
  --color-warning: #f59e0b;

  --radius-card: 12px;
  --radius-btn: 10px;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html,
body,
#root {
  height: 100%;
}

body {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 15px;
  line-height: 1.6;
  color: var(--color-text);
  background: var(--color-bg);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: rgba(15, 23, 42, 0.12);
  border-radius: 4px;
}
::-webkit-scrollbar-thumb:hover {
  background: rgba(15, 23, 42, 0.22);
}

button {
  font-family: inherit;
}

input,
textarea {
  font-family: inherit;
}

:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--color-primary) 60%, transparent);
  outline-offset: 2px;
}

/* ============================================================
   Markdown 回答渲染（react-markdown + remark-gfm，暗色主题）
   ============================================================ */

/* 覆盖 .chat-bubble-text 的 pre-wrap：markdown 段落由标签管理换行 */
.chat-bubble-text.md-content {
  white-space: normal;
}

.md-content > :first-child {
  margin-top: 0;
}
.md-content > :last-child {
  margin-bottom: 0;
}

/* 标题 */
.md-content h1,
.md-content h2,
.md-content h3 {
  color: var(--color-text);
  font-weight: 700;
  line-height: 1.35;
  margin: 18px 0 10px;
}
.md-content h1 {
  font-size: 20px;
}
.md-content h2 {
  font-size: 17px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--color-line);
}
.md-content h3 {
  font-size: 15.5px;
}

/* 列表 */
.md-content ul,
.md-content ol {
  margin: 6px 0 10px;
  padding-left: 1.5em;
}
.md-content ul {
  list-style: disc;
}
.md-content ol {
  list-style: decimal;
}
.md-content li {
  margin: 3px 0;
}
.md-content li > ul,
.md-content li > ol {
  margin: 3px 0;
}

/* 表格：边框 / 表头背景 / 斑马纹 */
.md-content table {
  width: 100%;
  margin: 10px 0;
  border-collapse: collapse;
  font-size: 13.5px;
}
.md-content th,
.md-content td {
  border: 1px solid var(--color-line-strong);
  padding: 7px 12px;
  text-align: left;
  vertical-align: top;
}
.md-content thead th {
  background: var(--color-surface-3);
  color: var(--color-text);
  font-weight: 600;
}
.md-content tbody tr:nth-child(even) {
  background: rgba(15, 23, 42, 0.03);
}

/* 代码块 / 行内代码 */
.md-content pre {
  background: #f1f5f9;
  border: 1px solid var(--color-line);
  border-radius: 10px;
  padding: 12px 14px;
  overflow-x: auto;
  margin: 10px 0;
  font-size: 13px;
  line-height: 1.6;
}
.md-content code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace;
  font-size: 0.92em;
}
.md-content :not(pre) > code {
  background: rgba(15, 23, 42, 0.08);
  border: 1px solid var(--color-line);
  border-radius: 6px;
  padding: 1px 6px;
  color: var(--color-accent);
}

/* 引用 */
.md-content blockquote {
  margin: 10px 0;
  padding: 8px 14px;
  border-left: 3px solid var(--color-primary);
  background: rgba(24, 226, 153, 0.06);
  border-radius: 0 8px 8px 0;
  color: var(--color-text-muted);
}
.md-content blockquote p {
  margin: 0;
}

/* 链接 */
.md-content a {
  color: var(--color-accent);
  text-decoration: none;
}
.md-content a:hover {
  text-decoration: underline;
}

/* 分隔线 */
.md-content hr {
  margin: 14px 0;
  border: 0;
  border-top: 1px solid var(--color-line-strong);
}

/* ============================================================
   训练计划 — 新增计划表单
   ============================================================ */
.plan-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
}
.plan-add-btn {
  align-self: flex-start;
  border: 1px solid var(--color-primary);
  background: rgba(24, 226, 153, 0.08);
  color: var(--color-primary);
  border-radius: var(--radius-btn);
  padding: 8px 16px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;
}
.plan-add-btn:hover {
  background: rgba(24, 226, 153, 0.18);
}
.plan-success-tip {
  font-size: 13px;
  color: var(--color-primary);
}
.plan-form {
  background: var(--color-surface);
  border: 1px solid var(--color-line);
  border-radius: var(--radius-card);
  padding: 16px;
}
.plan-form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}
.plan-form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.plan-form-field-wide {
  grid-column: 1 / -1;
}
.plan-form-label {
  font-size: 12.5px;
  color: var(--color-text-muted);
}
.plan-form-field input,
.plan-form-field select,
.plan-form-field textarea {
  background: var(--color-surface-2);
  border: 1px solid var(--color-line-strong);
  color: var(--color-text);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13.5px;
  outline: none;
}
.plan-form-field input:focus,
.plan-form-field select:focus,
.plan-form-field textarea:focus {
  border-color: var(--color-primary);
}
.plan-form-field textarea {
  resize: vertical;
  font-family: inherit;
}
.plan-form-error {
  margin-top: 12px;
  font-size: 13px;
  color: var(--color-danger);
}
.plan-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
}
.plan-form-cancel {
  border: 1px solid var(--color-line-strong);
  background: transparent;
  color: var(--color-text-muted);
  border-radius: var(--radius-btn);
  padding: 8px 18px;
  font-size: 13px;
  cursor: pointer;
}
.plan-form-submit {
  border: none;
  background: var(--color-primary);
  color: #052e16;
  border-radius: var(--radius-btn);
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.plan-form-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
